// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { FormFieldStyle } from "@insite/mobius/FormField";
import { PaginationStyle } from "@insite/mobius/Pagination";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import updateSearchFields from "@rc/App/Handlers/Pages/BackOrderList/UpdateSearchFields";
import { BackOrderDataViewContext } from "@rc/App/Store/Selectors/BackOrderSelector";
import Pagination, { PaginationPresentationProps } from "@rc/MobiusOverrides/Pagination/Pagination";
import { xScreen } from "@rc/XDL/Utilities";
import React, { FC, useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    updateSearchFields,
};

type Props = WidgetProps & ResolveThunks<typeof mapDispatchToProps>;

export interface BackOrderListPaginationStyles {
    pagination?: PaginationPresentationProps;
    wrapper?: InjectableCss;
}

export const backOrderListPaginationStyles: BackOrderListPaginationStyles = {
    pagination: {
        cssOverrides: {
            pagination: css`
                &&& {
                    margin: 0;
                    ${xScreen("gt", "sm")} {
                        justify-content: flex-end;
                        [class^='PerPageSelect'] ${FormFieldStyle} {
                            width: 200px;
                        }
                    }
                }
            `,
            linkList: css`
                ${xScreen("gt", "md")} {
                    margin-left: 38px;
                }
            `,
        },
    },
    wrapper: {
        css: css`
            &.bottom {
                margin-right: 10px;
                margin-top: 22px;

                ${PaginationStyle} {
                    justify-content: flex-end;
                }
            }
        `,  
    },
};

const styles = backOrderListPaginationStyles;

const BackOrderListPagination = ({ updateSearchFields, fields: { isBottom } }: Props) => {
    const backOrderListDataView = useContext(BackOrderDataViewContext);

    const changePage = (newPageIndex: number) => {
        updateSearchFields({
            page: newPageIndex,
        });
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        updateSearchFields({
            page: 1,
            pageSize: newPageSize,
        });
    };

    if (!backOrderListDataView.value) {
        return null;
    }

    const { pagination } = backOrderListDataView;
    if (!pagination || pagination.totalItemCount === 0) {
        return null;
    }

    return (
        <StyledWrapper className={ isBottom ? 'bottom' : '' } {...styles.wrapper}>
            <Pagination
                {...styles.pagination}
                resultsCount={pagination.totalItemCount}
                currentPage={pagination.page}
                resultsPerPage={pagination.pageSize}
                resultsPerPageOptions={pagination.pageSizeOptions}
                onChangePage={changePage}
                onChangeResultsPerPage={changeResultsPerPage}
                isBottom={isBottom}
            />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(BackOrderListPagination),
    definition: {
        group: "XNGAGE" as any,
        displayName: "BackOrder - Pagination",
        fieldDefinitions: [
            {
                name: "isBottom",
                displayName: "Pagination at the bottom?",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
            },
        ],
    },
};

export default widgetModule;
